<!-- @format -->
<template>
    <div
        id="app"
        :class="[
            pageClass,
            'page',
            { 'read-only': isReadOnlyUser },
            { 'under-review': isAccountUnderReview },
        ]"
        data-app
    >
        <account-review-banner v-show="isAccountUnderReview" />
        <div class="app-wrapper" :class="componentClass">
            <nav-bar v-if="isActive && isLoggedIn && isUiShown" ref="nav" />
            <mobile-header v-if="isLoggedIn && isUiShown && isActive" />
            <div
                class="main"
                :class="{ 'main-content': isLoggedIn && isUiShown, 'inactive-account': !isActive }"
                @click="minimizeNav"
            >
                <notification-banners></notification-banners>
                <app-header v-if="isLoggedIn && isUiShown" :key="clientId" />
                <router-view class="body-content" id="body-content" />
                <sliding-widgets />
                <modals-container class="modals-container" />
                <call-popup v-if="callActive" />
            </div>
        </div>
    </div>
</template>

<script>
import './scripts/filters';
import './assets/css/bootstrap.css';
import './assets/css/bootstrap-grid-v4.css';
import './assets/css/jquery-ui.css';
import './assets/css/font-awesome.css';
import './assets/css/froala.css';
import './assets/css/site.css';
import './assets/css/module.css';
import './assets/css/trix.css';
import './assets/css/colorpicker.css';
import './assets/css/rangeslider.css';
import './assets/css/integration-setups.css';
import './assets/css/bootstrap-datepicker.css';
import './assets/css/integration-settings-page.css';
import './assets/css/modal-module.css';
import './assets/css/small-module.css';
import './assets/css/table-module.css';
import './assets/css/segmented-table-module.css';
import './assets/css/monthly-revenue-module.css';
import './assets/css/step-module.css';
import './scripts/bootstrap.min';

const AccountReviewBanner = () => import('@/components/app-header/account-review-banner');
const NavBar = () => import('@/components/side-navigation/nav-bar');
const MobileHeader = () => import('@/components/mobile-navigation/mobile-header');
const AppHeader = () => import('@/components/app-header/app-header');
import NotificationBanners from '@/components/notification/notification-banners';
import SlidingWidgets from '@/components/sliding-widgets/sliding-widgets';
import CallPopup from '@/components/popups/call/call-popup.vue';
import {
    WoliService,
    WOLI_OPEN_EVENT,
    WOLI_CLOSED_EVENT,
} from '@/scripts/services/woli/woli-service';

import EventBus from '@/scripts/event-bus';
import WoliPopup from '@/components/popups/woli/woli-popup.vue';

export default {
    components: {
        'nav-bar': NavBar,
        'app-header': AppHeader,
        'mobile-header': MobileHeader,
        'notification-banners': NotificationBanners,
        'sliding-widgets': SlidingWidgets,
        'account-review-banner': AccountReviewBanner,
        'call-popup': CallPopup,
    },

    data() {
        return {
            componentClass: [],
            intervalId: null,
            privacySessionId: null,
            callActive: false,
        };
    },

    watch: {
        $route(to) {
            if (to.query.widget) {
                EventBus.$emit('open-widget', JSON.parse(decodeURIComponent(to.query.widget)));
                this.$router.replace(`${to.path}?${this.getQuery(to.query, 'widget')}`);
            }
        },
    },

    created() {
        this.$store.dispatch('theme/loadTheme');

        EventBus.$on('open-widget', this.updateComponentClass);
        EventBus.$on('close-widget', this.clearComponentClass);
        EventBus.$on('close-widgets', this.clearComponentClass);
        EventBus.$on('privacy-session-started', this.handlePrivacySessionEvent);
        EventBus.$on('open-call-module', this.handleCallOpen);
        EventBus.$on('close-call-module', this.handleCallClose);
        EventBus.$on(WOLI_OPEN_EVENT, this.handleWoliOpen);
    },

    beforeDestroy() {
        EventBus.$off('open-widget', this.updateComponentClass);
        EventBus.$off('close-widget', this.clearComponentClass);
        EventBus.$off('close-widgets', this.clearComponentClass);
        EventBus.$off('privacy-session-started', this.handlePrivacySessionEvent);
        EventBus.$off('open-call-module', this.handleCallOpen);
        EventBus.$off('close-call-module', this.handleCallClose);
        EventBus.$off(WOLI_OPEN_EVENT, this.handleWoliOpen);

        clearInterval(this.intervalId);

        WoliService.closeWoliPopup();
    },

    computed: {
        isLoggedIn() {
            return this.$store.state.isLoggedIn;
        },

        isUiShown() {
            return this.$route.meta.isUiShown;
        },

        pageClass() {
            return this.$route.meta.pageClass;
        },

        isActive() {
            if (this.$store.getters.isClientUser) {
                return this.$store.state.context.client.isActive;
            } else {
                return true;
            }
        },

        isClientUser() {
            return this.$store.getters.isClientUser;
        },

        isReadOnlyUser() {
            return this.$store.getters.isReadOnlyUser;
        },

        isAccountUnderReview() {
            return this.$store.state.context.client.underReview;
        },

        clientId() {
            return this.$store.state.context.client.id;
        },
    },

    methods: {
        handlePrivacySessionEvent(intervalIdFromLS) {
            this.privacySessionId = sessionStorage.getItem('privacy_session_id');
            this.intervalId = intervalIdFromLS;
            const lastCheckInterval = sessionStorage.getItem('privacy_last_check_date');

            if (this.intervalId) {
                clearInterval(this.intervalId);
                this.intervalId = null;
                sessionStorage.removeItem('privacy_interval_id');
            }

            this.intervalId = setInterval(
                () => {
                    const currentInterval = Date.now();
                    const diff = Math.ceil((currentInterval - lastCheckInterval) / (1000 * 60));
                    sessionStorage.setItem('privacy_interval_id', this.intervalId);
                    if (diff >= 55) {
                        sessionStorage.removeItem('privacy_session_id');
                        sessionStorage.removeItem('privacy_interval_id');
                        sessionStorage.removeItem('privacy_last_check_date');
                        this.$router.push('/');
                        clearInterval(this.intervalId);
                        this.privacySessionId = null;
                        this.intervalId = null;
                        return;
                    }
                },
                60 * 1000 * 5,
            );
            sessionStorage.setItem('privacy_interval_id', this.intervalId);
        },

        minimizeNav() {
            EventBus.$emit('navigation:close');
        },

        updateComponentClass(widget) {
            var className = widget.name + '-active';
            this.componentClass = [className, 'widget-active'];
        },

        clearComponentClass() {
            this.componentClass = [];
        },

        getQuery(queryObj, exclude) {
            const queryKeys = Object.keys(queryObj);
            const queryString = queryKeys
                .map((key) => {
                    if (key !== exclude) {
                        return encodeURIComponent(key) + '=' + encodeURIComponent(queryObj[key]);
                    }
                })
                .join('&');

            return queryString;
        },

        handleWoliOpen(config) {
            this.$modal.show(
                WoliPopup,
                {
                    hasHeader: false,
                    createAction: false,
                    ...(config ?? {}),
                },
                {
                    height: '96%',
                    width: '90%',
                    clickToClose: false,
                },
                {
                    'before-close': () => {
                        EventBus.$emit(WOLI_CLOSED_EVENT, WoliPopup.name);
                    },
                },
            );
        },

        handleCallOpen(config) {
            this.$modal.show(
                CallPopup,
                {
                    hasHeader: false,
                    createAction: false,
                    ...(config ?? {}),
                },
                {
                    height: 'auto',
                    width: '688px',
                    clickToClose: false,
                    draggable: true,
                    resizable: true,
                },
            );
        },
    },
};
</script>

<style>
#app {
    width: 100%;
    height: 100%;
    background-color: var(--color-background-secondary);
}

#app.under-review {
    height: calc(100% - 42px);
}

body {
    overflow: hidden;
}

body.modal-open,
.modal-open .navbar-fixed-top,
.modal-open .navbar-fixed-bottom {
    margin-right: 0;
}

.modal::-webkit-scrollbar {
    display: none;
}

.body-content {
    overflow-x: hidden;
}

.body-content::-webkit-scrollbar {
    width: 9px;
    height: 9px;
}

.body-content::-webkit-scrollbar-track {
    border-radius: 32px;
}

.body-content::-webkit-scrollbar-thumb {
    border-radius: 32px;
    background: var(--color-background-scrollbar-default);
}

#app .app-wrapper {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    z-index: 10;
}

.main {
    height: 100%;
    width: 100%;
    background-color: var(--color-background-default);
}

.modals-container .v--modal,
.modals-container .v--modal-box {
    border-radius: 34px;
}

.modals-container .v--modal-overlay {
    z-index: 10045;
}

.site-navigation {
    display: none;
}

.header-module {
    display: none;
}

.main-content {
    position: relative;
    overflow: auto;
    flex: 1;
    z-index: 1000;
}

#intercom-container {
    visibility: hidden;
}

@media only screen and (min-width: 720px) {
    #intercom-container {
        visibility: visible;
    }

    #app .app-wrapper {
        flex-direction: row;
    }

    .body-content {
        height: calc(100% - 50px);
    }

    .main {
        box-shadow: -2px 0px 5px var(--color-box-shadow-dropdown);
    }

    .mobile-header {
        display: none;
    }

    .site-navigation {
        display: block;
    }

    .header-module {
        display: block;
    }
}

@media only screen and (max-width: 720px) {
    .notification-view-widget-active .mobile-header {
        z-index: 1;
    }

    .notification-view-widget-active .main-content {
        z-index: 2;
    }

    .body-content {
        padding: 65px 0 15px 0;
    }

    .navbar,
    .main .navbar.app-header .container {
        min-height: 40px;
    }

    .main .navbar.app-header .container {
        height: initial;
        background: var(--color-background-default);
    }

    #app .nav-bar {
        display: none;
    }

    .navbar .client-container {
        height: initial;
        margin: 0;
        max-width: 50%;
        width: 100%;
        min-width: 190px;
    }

    .navbar.app-header .container {
        height: 50px;
        display: flex;
        align-items: flex-start;
        align-items: center;
        flex-wrap: wrap;
    }

    .navbar.app-header .viewing-container {
        margin-bottom: 0px;
        padding-left: 10px;
        display: flex;
    }

    .navbar .search-container {
        margin-top: 10px;
    }

    .main-content {
        overflow: initial;
    }
}

@media only screen and (max-width: 420px) {
    .navbar {
        height: 110px;
    }
}
</style>
